/**
 * Форматы URL-адреса ссылки NormaCS на документ:
 * 1) Абсолютный URL-адрес документа (фрагмента документа):
 *    normacs://{db_domain}/{db_document_id}?{params}#{fragment_id}
 *    Примечание:
 *    символы "//" после "normacs:" могут отсутствовать.
 * 2) Относительный URL-адрес (внутренняя ссылка на фрагмент документа):
 *    #{fragment_id}
 *
 * Где:
 * db_domain - домен базы документов;
 * db_document_id - идентификатор документа в базе;
 * params - параметры URL;
 * fragment_id - идентификатор фрагмента документа.
 */

/**
 * Регулярное выражение абсолютного URL-адреса ссылки на документ.
 * @type {RegExp}
 */
const NORMACS_URL_REGEXP = /^\s*normacs:\/?\/?([^/?#]+)\/([^/?#]+)[^#]*#?(\S+)?/i;
const NORMACS_URL_REGEXP_DB_DOMAIN = 1;
const NORMACS_URL_REGEXP_DOCUMENT_ID = 2;
const NORMACS_URL_REGEXP_FRAGMENT_ID = 3;

const kodeksUrl = "kodeks://link";

/**
 * Объект ссылки NormaCS.
 *
 * @property {string} url - Исходная строка URL-адреса ссылки.
 *
 * @property {string|null} documentId - Идентификатор документа или значение null,
 * если URL не является ссылкой на документ в формате NormaCS.
 *
 * @property {string|null} documentDbId - Идентификатор базы документа или значение null,
 * если URL не является ссылкой на документ в формате NormaCS.
 *
 * @property {string|null} fragmentId - Идентификатор фрагмента документа или значение null,
 * если URL не является ссылкой на документ в формате NormaCS или фрагмент не указан.
 *
 * @property {boolean} isInternal - Признак внутренней ссылки (документ ссылается сам на себя).
 *
 * @property {string} documentView - Вид документа ("card", "scan", "text").
 */
export default class ApiNormaCsLink {
  /**
   * Разбирает строку URL-адреса в формате NormaCS и создает соответствующий ей объект ссылки.
   *
   * @param {string} normaCsUrl - Строка URL-адреса ссылки.
   * @param {?string} fromDocumentId - Идентификатор документа, в котором находится ссылка,
   * или значение null, если ссылка находится не в тексте документа.
   * @param {Object<string,string>} databaseIds - Справочник соответствий доменов и идентификаторов
   * баз документов.
   */
  constructor(normaCsUrl, fromDocumentId, databaseIds) {
    let documentId = null,
      documentDbId = null,
      fragmentId = null,
      isInternal = false,
      documentView = 'text';
	  
    if (normaCsUrl[0] === '#') {
      documentId = fromDocumentId;
      documentDbId = fromDocumentId ? fromDocumentId.split('.')[0] : null;
      fragmentId = normaCsUrl.slice(1);
      isInternal = !!fromDocumentId;
    } 
    else {
      let urlMatch = normaCsUrl.match(NORMACS_URL_REGEXP);
      if (urlMatch) {
		    // Cсылка normacs
        // Получаем идентификатор базы по домену базы из URL ссылки.
        documentDbId =
          databaseIds[urlMatch[NORMACS_URL_REGEXP_DB_DOMAIN]] ||
          urlMatch[NORMACS_URL_REGEXP_DB_DOMAIN];

        // Абсолютный идентификатор документа состоит из идентификатора
        // базы документов и идентификатора документа в этой базе,
        // разделенных точкой.
        documentId =
          documentDbId + '.' + urlMatch[NORMACS_URL_REGEXP_DOCUMENT_ID];

        if (urlMatch[NORMACS_URL_REGEXP_FRAGMENT_ID] && normaCsUrl.indexOf("#_tiff_page") == -1) {
          fragmentId = urlMatch[NORMACS_URL_REGEXP_FRAGMENT_ID];
        }
        let i1 = normaCsUrl.indexOf("#_tiff_page");
        if (i1 != -1) {
          documentView = 'scan';
          if (normaCsUrl.substr(i1 + 11) != "1")
            fragmentId = "#" + normaCsUrl.substr(i1 + 11);
        }
        else if (normaCsUrl.indexOf("#annotation") != -1)
          documentView = 'card';
        else if (normaCsUrl.indexOf("/x/ATTACH/") != -1) {
          let i1 = normaCsUrl.lastIndexOf("/");
          let i2 = normaCsUrl.length;
          documentId = "1.=" + normaCsUrl.substr(i1 + 1, i2 - i1);
        }
        isInternal = documentId === fromDocumentId;
      }
	    else if (normaCsUrl.indexOf(kodeksUrl) === 0){
        // ссылка kodex
        documentDbId = 1;
        let i1 = normaCsUrl.indexOf("nd=");
        if (i1 != -1) {
          i1 += 3;
          let i2 = normaCsUrl.indexOf("&", i1 + 1);
          if (i2 === -1) i2 = normaCsUrl.length;
          documentId = "1.-" + normaCsUrl.substr(i1, i2 - i1);
          i1 = normaCsUrl.indexOf("mark=");
          if (i1 != -1) {
            fragmentId = normaCsUrl.substr(i1 + 5);
          }
        }
		  }
    }

    this.url = normaCsUrl;
    this.documentId = documentId;
    this.documentDbId = documentDbId;
    this.fragmentId = fragmentId;
    this.isInternal = isInternal;
    this.documentView = documentView;
  }

  /**
   * Возвращает значение true, если это ссылка на документ в формате
   * десктоп-приложения NormaCS. Иначе возвращает false (в том числе
   * для ссылок на документы в формате веб-клиента).
   *
   * @returns {boolean}
   */
  isLinkToDocument() {
    return !!this.documentId;
  }
}
